"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { StickyNoteIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useMemoRelatedContexts } from "../../memos/memo-events";
import { memoContextKey, memoTargetFromPathname } from "../memo-node-id";
import { useWorkspaceSidePanelMode } from "../workspace-side-panel-mode";

/**
 * workspace header の主導線として User memo list を開く icon button です。
 * 画面内カードに同じ導線を増やさず、route から page-level memo target を復元できる画面だけで表示します。
 */
export function MemoHeaderToggle() {
  const pathname = usePathname();
  const { i18n } = useLingui();
  const {
    isOpen,
    mode,
    userMemoIntent,
    openUserMemo,
    closeWorkspaceSidePanel,
  } = useWorkspaceSidePanelMode();
  const memoTarget = memoTargetFromPathname(pathname);
  const memoTargetContext = useMemo(
    () =>
      memoTarget
        ? {
            workspaceId: memoTarget.workspaceId,
            featureKind: memoTarget.featureKind,
            targetKind: memoTarget.targetKind,
            targetId: memoTarget.targetId,
            projectId: memoTarget.projectId,
            searchAnalyticsId: "",
            viewName: "",
          }
        : null,
    [memoTarget],
  );
  const relatedContexts = useMemoRelatedContexts(memoTargetContext);
  const memoTargetKey = memoTargetContext
    ? memoContextKey(memoTargetContext)
    : null;
  const currentIntentKey = userMemoIntent?.context
    ? memoContextKey(userMemoIntent.context)
    : null;
  const isActive =
    isOpen &&
    mode === "MEMO" &&
    currentIntentKey !== null &&
    currentIntentKey === memoTargetKey;

  if (!memoTargetContext) {
    return null;
  }

  const label = isActive
    ? i18n._(msg`Close memo list`)
    : i18n._(msg`Open memo list`);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={label}
          aria-pressed={isActive}
          onClick={
            isActive
              ? closeWorkspaceSidePanel
              : () =>
                  openUserMemo({
                    state: "list",
                    context: memoTargetContext,
                    relatedContexts:
                      relatedContexts.length > 0
                        ? [...relatedContexts]
                        : undefined,
                  })
          }
          className={cn(
            "shadow-none",
            isActive &&
              "border-amethyst-primary/80 bg-amethyst-extra-light text-amethyst-primary hover:bg-amethyst-extra-light hover:text-amethyst-primary dark:border-amethyst-light/80 dark:bg-amethyst-primary/25 dark:text-amethyst-semi-light",
          )}
        >
          <StickyNoteIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
